import type { SupporterDetailsData } from '../queries.ts';

/**
 * Regex to strip noise words before deriving initials.
 * Case-insensitive, matches words/titles only as standalone terms.
 */
const noiseWordsRegex = /(?:\b(and|the)\b|\b(mrs|mr|ms|dr)\b\.?|&)/gi;
const alphanumericCharacterRegex = /[\p{L}\p{N}]/u;

/**
 * Derive up to 2 uppercase initials from a name string.
 * Strips noise words, then takes the first letter or number from each remaining word.
 */
export const deriveInitialsFromName = (name: string): string =>
  name
    .replaceAll(noiseWordsRegex, '')
    .split(/\s+/)
    .map(
      (word) =>
        word.match(alphanumericCharacterRegex)?.[0]?.toUpperCase() ?? '',
    )
    .join('')
    .slice(0, 2);

export const getSupporterFullName = (
  details: SupporterDetailsData | null | undefined,
): string | undefined => {
  if (!details) {
    return undefined;
  }

  if (details.organizationName) {
    return details.organizationName;
  }

  const fullName = [
    details.salutation,
    details.firstName,
    details.lastName,
    details.suffix,
  ]
    .filter(Boolean)
    .join(' ');

  return fullName || undefined;
};

export const getSupporterInitials = (
  details: SupporterDetailsData | null | undefined,
): string | undefined => {
  if (!details) {
    return undefined;
  }

  // Business/group accounts: derive from organization name
  if (details.organizationName) {
    return deriveInitialsFromName(details.organizationName) || undefined;
  }

  // Contact: prefer preferredName if populated
  if (details.preferredName) {
    const initials = deriveInitialsFromName(details.preferredName);
    // If preferredName yields only one initial, supplement with lastName
    if (initials.length === 1 && details.lastName) {
      return `${initials}${details.lastName.charAt(0).toUpperCase()}`;
    }
    return initials || undefined;
  }

  // Fallback: first + last name
  const nameSource = [details.firstName, details.lastName]
    .filter(Boolean)
    .join(' ');

  if (!nameSource) {
    return undefined;
  }

  return deriveInitialsFromName(nameSource) || undefined;
};

export const getSupporterPreferredName = (
  details: SupporterDetailsData | null | undefined,
): string | undefined => {
  if (!details) {
    return undefined;
  }

  return details.organizationName || details.preferredName || undefined;
};

export const getSupporterEmail = (
  details: SupporterDetailsData | null | undefined,
): string | undefined => {
  if (!details) {
    return undefined;
  }

  return details.email || undefined;
};
